import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { sampleEdgePoints } from '../pointCloudUtils';
import { BATTERY_DIM, BATTERY_PRIMARY, BATTERY_LOW, BATTERY_MID, BATTERY_GLOW } from '../twinColors';
const CONTAINER_W = 6;
const CONTAINER_H = 3;
const CONTAINER_D = 2.5;

// Pre-allocated color for lerp
const _colorA = new THREE.Color();
const _colorB = new THREE.Color();

/**
 * SOC color: dark blue(0) -> medium blue(0.5) -> bright blue(1.0)
 */
function socToColor(soc) {
    if (soc > 0.5) {
        const t = (soc - 0.5) * 2;
        _colorA.set(BATTERY_MID);
        _colorB.set(BATTERY_PRIMARY);
        return _colorA.lerp(_colorB, t);
    }
    const t = soc * 2;
    _colorA.set(BATTERY_LOW);
    _colorB.set(BATTERY_MID);
    return _colorA.lerp(_colorB, t);
}

/**
 * BatteryContainer - Point cloud shipping container with SOC fill,
 * internal cell divider particles, and pulse ring on charge/discharge.
 */
export default function BatteryContainer({ state, position, onClick }) {
    const containerRef = useRef();
    const fillRef = useRef();
    const ringRef = useRef();
    const ringScaleRef = useRef(1);

    const soc = useMemo(() => {
        const val = state?.soc ?? 0;
        return Math.min(1, Math.max(0, val));
    }, [state?.soc]);

    const isActive = Math.abs(state?.power_kw ?? 0) > 5;

    // Sample container edge points (dispose intermediate geometries)
    const containerPoints = useMemo(() => {
        const box = new THREE.BoxGeometry(CONTAINER_W, CONTAINER_H, CONTAINER_D);
        const edges = new THREE.EdgesGeometry(box);
        const pts = sampleEdgePoints(edges, 400, 0.06);
        box.dispose();
        edges.dispose();
        return pts;
    }, []);

    // Cell divider points
    const dividerPoints = useMemo(() => {
        const pts = [];
        const dividerXs = [-1.8, -0.6, 0.6, 1.8];
        for (const dx of dividerXs) {
            for (let i = 0; i < 15; i++) {
                pts.push(
                    dx + (Math.random() - 0.5) * 0.04,
                    (Math.random() - 0.5) * CONTAINER_H,
                    (Math.random() - 0.5) * 0.04
                );
            }
        }
        return new Float32Array(pts);
    }, []);

    const socColor = useMemo(() => socToColor(soc), [soc]);
    const socHex = useMemo(() => `#${socColor.getHexString()}`, [socColor]);

    useFrame(() => {
        // SOC fill plane position
        if (fillRef.current) {
            const fillY = 0.15 + soc * 2.7;
            fillRef.current.position.y = fillY - CONTAINER_H / 2;
            fillRef.current.material.opacity = 0.15 + soc * 0.3;
        }

        // Pulse ring animation
        if (ringRef.current) {
            if (isActive) {
                ringRef.current.visible = true;
                ringScaleRef.current += 0.008;
                if (ringScaleRef.current > 2) ringScaleRef.current = 1;
                const t = ringScaleRef.current - 1;
                const s = ringScaleRef.current;
                ringRef.current.scale.set(s, s, s);
                ringRef.current.material.opacity = 0.2 * (1 - t);
            } else {
                ringRef.current.visible = false;
            }
        }
    });

    return (
        <group position={position} onClick={onClick}>
            <group position={[0, 1.5, 0]}>
                {/* Container particle cloud */}
                <points ref={containerRef}>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            count={containerPoints.length / 3}
                            array={containerPoints}
                            itemSize={3}
                        />
                    </bufferGeometry>
                    <pointsMaterial
                        color={BATTERY_DIM}
                        size={0.1}
                        transparent
                        opacity={0.7}
                        depthWrite={false}
                        sizeAttenuation
                        blending={THREE.AdditiveBlending}
                    />
                </points>

                {/* Cell divider particles */}
                <points>
                    <bufferGeometry>
                        <bufferAttribute
                            attach="attributes-position"
                            count={dividerPoints.length / 3}
                            array={dividerPoints}
                            itemSize={3}
                        />
                    </bufferGeometry>
                    <pointsMaterial
                        color={BATTERY_DIM}
                        size={0.06}
                        transparent
                        opacity={0.25}
                        depthWrite={false}
                        sizeAttenuation
                        blending={THREE.AdditiveBlending}
                    />
                </points>

                {/* SOC fill plane */}
                <mesh ref={fillRef}>
                    <planeGeometry args={[5.6, 0.08]} />
                    <meshBasicMaterial
                        color={socHex}
                        transparent
                        opacity={0.3}
                        depthWrite={false}
                        blending={THREE.AdditiveBlending}
                    />
                </mesh>

                {/* Pulse ring (horizontal torus) */}
                <mesh ref={ringRef} rotation={[-Math.PI / 2, 0, 0]}>
                    <torusGeometry args={[3.5, 0.04, 8, 32]} />
                    <meshBasicMaterial
                        color={BATTERY_PRIMARY}
                        transparent
                        opacity={0.2}
                        depthWrite={false}
                        blending={THREE.AdditiveBlending}
                    />
                </mesh>
            </group>
        </group>
    );
}
